import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import PageMeta from "@/components/PageMeta";
import { ShieldCheck, Lock, Clock, EyeSlash, Warning, ArrowRight } from "@phosphor-icons/react";

const Pillar = ({ icon: Icon, title, children, testid }) => (
  <div className="brut-card p-6" data-testid={testid}>
    <div className="w-10 h-10 grid place-items-center bg-[#FF3366]/10 text-[#FF3366] mb-3">
      <Icon size={18} weight="duotone" />
    </div>
    <h3 className="text-lg font-bold tracking-tight mb-2">{title}</h3>
    <div className="text-sm text-black/70 leading-relaxed space-y-2">{children}</div>
  </div>
);

const H2 = ({ children, id }) => (
  <h2 id={id} className="text-2xl md:text-3xl tracking-tight mt-12 mb-4">{children}</h2>
);

const P = ({ children }) => (
  <p className="text-[16px] leading-[1.75] text-black/80 mb-4">{children}</p>
);

export default function Security() {
  return (
    <PageShell
      eyebrow="Security & privacy"
      title="How SendMe protects your files."
      subtitle="A plain-English explanation of the security model, the threats we defend against, and the threats we explicitly don't. Read this before sending anything sensitive."
    >
      <PageMeta
        title="Security & privacy model"
        description="How SendMe encrypts files in transit (TLS 1.3) and at rest (AES-256-GCM). Threat model, non-guessable URLs, bcrypt password hashing, and responsible disclosure policy."
        keywords="secure file transfer, AES 256 encryption, TLS 1.3, bcrypt, GDPR file sharing"
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12" data-testid="security-pillars">
        <Pillar icon={Lock} title="Encrypted in transit" testid="pillar-tls">
          <p>Every byte travels over HTTPS with TLS 1.3 between your browser and our servers. Anyone snooping on your Wi-Fi sees only ciphertext.</p>
        </Pillar>
        <Pillar icon={ShieldCheck} title="Encrypted at rest" testid="pillar-aes">
          <p>Files are stored on AES-256-GCM encrypted object storage. The raw disks are unreadable without our key-management infrastructure.</p>
        </Pillar>
        <Pillar icon={Clock} title="Expiry by default" testid="pillar-expiry">
          <p>Every transfer has a deadline. After expiry the file becomes inaccessible to receivers and is purged from active storage shortly after.</p>
        </Pillar>
        <Pillar icon={EyeSlash} title="Non-guessable URLs" testid="pillar-urls">
          <p>Storage paths use random UUIDs. Receivers never see them — they only see your 6-digit code, which our backend translates internally.</p>
        </Pillar>
        <Pillar icon={Lock} title="Bcrypt password hashing" testid="pillar-bcrypt">
          <p>Optional transfer passwords are stored as bcrypt hashes (cost factor 12). We cannot recover or reset them; neither can attackers.</p>
        </Pillar>
        <Pillar icon={Warning} title="Rate-limited endpoints" testid="pillar-ratelimit">
          <p>Code lookups, password verifications, and OTP requests are all rate-limited to prevent brute-force enumeration.</p>
        </Pillar>
      </div>

      <div className="max-w-3xl">
        <H2 id="threat-model">Our threat model — and what's NOT in it</H2>
        <P>
          Every security tool has a threat model — a list of attackers it's designed to stop, and a list it explicitly isn't. SendMe is built to defeat opportunistic attackers and passive surveillance, not state-level adversaries. Concretely:
        </P>
        <P>
          <strong>We defend against:</strong> someone sniffing your Wi-Fi at a café; someone guessing or brute-forcing your 6-digit code; someone obtaining a leaked link from a chat archive after expiry; an internal employee at our hosting provider walking off with a raw disk; opportunistic web scrapers indexing public URLs.
        </P>
        <P>
          <strong>We do NOT defend against:</strong> a determined attacker who compromises your device before the file is encrypted; a court order compelling us to disclose a specific transfer before it expires; a recipient who screenshots the file and shares the screenshot; a sophisticated state-level adversary with access to undisclosed cryptographic vulnerabilities.
        </P>
        <P>
          For threats outside our model, layer additional protection: encrypt files client-side with a tool like 7-Zip or VeraCrypt before uploading, and share the encryption password through a fully separate channel (a voice call, an in-person handoff, or a different end-to-end-encrypted messenger).
        </P>

        <H2 id="data-collected">What we collect — and what we don't</H2>
        <P>
          For each transfer we store: the file content, its size, its content-type, a server-generated 6-digit code, the expiry timestamp, an optional bcrypt-hashed password, and a created-at timestamp. We do not store the file name (we use a UUID internally) — though we do return the original name to the receiver so the download works correctly.
        </P>
        <P>
          For each registered user we store: email, name, hashed password (for email/password accounts) or Google ID (for Google sign-in), and basic plan/billing metadata. We do not store payment card numbers — Razorpay handles those.
        </P>
        <P>
          We do not run third-party advertising trackers. We do use a single first-party analytics tool (Google Analytics 4) to understand which pages people read — pageviews and a few custom events like "file_upload". We use Google AdSense on blog pages only, and we serve non-personalised ads to EU visitors by default.
        </P>

        <H2 id="end-to-end-encryption">Why we're not end-to-end encrypted (yet)</H2>
        <P>
          True end-to-end encryption (E2EE) means the service operator cannot read your file even if they wanted to. The encryption key lives only on the sender's and receiver's devices. This is the gold standard, and we plan to ship it.
        </P>
        <P>
          E2EE adds friction that doesn't fit a "no signup, type a code" product. The receiver needs to also hold a key, which means either both sides need to install software, or the key has to be embedded in the URL fragment (after the `#`) — which then exposes the entire URL as a sensitive secret. We're working on a model where the encryption key lives in the URL fragment and the 6-digit code is a separate authorisation step. That's planned for late 2026, with Pro subscribers getting early access.
        </P>
        <P>
          In the meantime, our "good-enough" model relies on (a) short expiry, (b) optional password protection, and (c) operator-side encryption-at-rest. For most users this is comfortably above the bar set by email and chat-based file sharing.
        </P>

        <H2 id="compliance">Compliance and certifications</H2>
        <P>
          SendMe is GDPR-compliant by design. We collect only the data necessary to run the service, we delete it according to a published schedule (transfer expiry and account deletion), and we honour data-subject requests within 30 days. Write to <a className="underline" href="mailto:privacy@sendme.co.in">privacy@sendme.co.in</a> to exercise any GDPR right.
        </P>
        <P>
          We are not currently SOC 2 or ISO 27001 certified — certification is on the roadmap for 2027 as we scale into more enterprise-style team plans. If your organisation requires a security questionnaire before adopting, write to <a className="underline" href="mailto:security@sendme.co.in">security@sendme.co.in</a> and we'll send a SIG-Lite response.
        </P>

        <H2 id="disclosure">Responsible disclosure</H2>
        <P>
          We don't run a formal bug bounty programme yet, but we treat security reports with priority. Write to <a className="underline" href="mailto:security@sendme.co.in">security@sendme.co.in</a> with a detailed reproduction. We respond within 24 hours, fix critical issues within 7 days, and credit reporters who request it. We won't pursue legal action against good-faith researchers.
        </P>
      </div>

      <div className="mt-14 border border-black p-6 md:p-8 text-center">
        <div className="small-caps mb-3 text-[#FF3366]">Try it</div>
        <h3 className="text-2xl md:text-3xl tracking-tight mb-3">Send your next file the right way.</h3>
        <p className="text-black/65 mb-5 max-w-lg mx-auto">No sign-up. 6-digit code. Auto-expiry. Optional password.</p>
        <Link to="/" className="btn-brut-primary inline-flex items-center gap-2">Start sending <ArrowRight size={16} weight="bold"/></Link>
      </div>
    </PageShell>
  );
}
